import { Grid, IconButton, Typography, Stack, SelectChangeEvent, MenuItem, FormControl, InputLabel, Select, TextField } from "@mui/material";
import { useEffect, useState, ChangeEvent } from "react";
import { capitalize } from "lodash";
import EditIcon from '@mui/icons-material/Edit';

import { FunctionalReconciliationMode, NormalizedDocumentType, OutboundAcknowledgementType, PartnerModel, PartnerTransactionModel, PartnerTransactionSchemaModel, PipelineModel, TransactionDirection, UpdatePartnerTransactionInput } from "../../gql-types.generated";
import { DialogDynamicListHeader, DialogDataGridWrapper } from "../../util/SharedStyles";
import AddEditDialog from "./AddEditDialog";
import TransactionPipelineDialog from "./TransactionPipelineDialog";
import DialogPartnerTransactionSchemaList from "../lists/transactions/DialogPartnerTransactionSchemaList";

interface EditPartnerTransactionDialogProps {
    isOpen: boolean;
    partner: PartnerModel | undefined;
    partnerTransaction: PartnerTransactionModel | undefined;
    transactionSchemas?: PartnerTransactionSchemaModel[] | undefined;
    onClose: () => void;
    onSave: (
        inputData: UpdatePartnerTransactionInput
    ) => void;
    refreshPartnerData: () => void;
    error?: Error | undefined;
}

const EditPartnerTransactionDialog: React.FC<EditPartnerTransactionDialogProps> = props => {
    const { isOpen, partner, partnerTransaction, transactionSchemas, onClose, onSave, refreshPartnerData, error } = props;
    const [isFormDirty, setIsFormDirty] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [partnerTransactionId, setPartnerTransactionId] = useState<string>('');
    const [transactionDirection, setTransactionDirection] = useState<TransactionDirection | undefined>(undefined);
    const [transactionName, setTransactionName] = useState<string | undefined>(undefined);
    const [transactionDocumentType, setTransactionDocumentType] = useState<NormalizedDocumentType | undefined>(undefined);
    const [pipelineIdValue, setPipelineIdValue] = useState<string>('');
    const [pipelineDescription, setPipelineDescription] = useState<string>('');
    const [pipelineDialogOpen, setPipelineDialogOpen] = useState(false);
    const [acknowledgementMode, setAcknowledgementMode] = useState<FunctionalReconciliationMode>(FunctionalReconciliationMode.Na);
    const [acknowledgementTimeInterval, setAcknowledgementTimeInterval] = useState<number | undefined>(undefined);
    const [outboundAcknowledgementOption, setOutboundAcknowledgementOption] = useState<OutboundAcknowledgementType | undefined>(OutboundAcknowledgementType.Normal);
        
    useEffect(() => {
        if (!isOpen) {
            setSubmitted(false);
            setIsFormDirty(false);
            setPipelineIdValue('');
            setPipelineDescription('');
            setAcknowledgementMode(FunctionalReconciliationMode.Na);
            setAcknowledgementTimeInterval(undefined);
            setOutboundAcknowledgementOption(undefined);
            setTransactionDirection(undefined);
            setTransactionName(undefined);
            setTransactionDocumentType(undefined);
        } 
    }, [isOpen]);

    useEffect(() => {
        if (partnerTransaction) {
            setPartnerTransactionId(partnerTransaction.id);
            setTransactionName(partnerTransaction.transaction?.name as string);
            setTransactionDirection(partnerTransaction.transaction?.direction as TransactionDirection);
            setTransactionDocumentType(partnerTransaction.transaction?.normalizedDocumentType as NormalizedDocumentType);
            if (partnerTransaction.pipeline) {
                setPipelineIdValue(partnerTransaction.pipeline.id);
                setPipelineDescription(partnerTransaction.pipeline.description as string);
            }
            if (partnerTransaction.functionalReconciliationMode) {
                setAcknowledgementMode(partnerTransaction.functionalReconciliationMode);
            } else if (partner?.functionalReconciliationMode) {
                setAcknowledgementMode(partner.functionalReconciliationMode);
            }
            if (partnerTransaction.acknowledgementTimeInterval) {
                setAcknowledgementTimeInterval(partnerTransaction.acknowledgementTimeInterval);
            } else if (partner?.acknowledgementTimeInterval) {
                setAcknowledgementTimeInterval(partner.acknowledgementTimeInterval);
            } else {
                setAcknowledgementTimeInterval(undefined);
            }
            if (partnerTransaction.outboundAcknowledgementOption) {
                setOutboundAcknowledgementOption(partnerTransaction.outboundAcknowledgementOption);
            }
        }
    }, [partnerTransaction]);

    const isInboundTransaction = transactionDirection === TransactionDirection.Inbound;
    const isFunctionalAcknowledgementTransaction = transactionDocumentType === NormalizedDocumentType.FunctionalAcknowledgement;

    const submitForm = () => {
        if (isFormDirty) {
            setSubmitted(true);
            onSave(
                {
                    id: partnerTransactionId,
                    pipelineId: pipelineIdValue,
                    functionalReconciliationMode: acknowledgementMode,
                    acknowledgementTimeInterval: acknowledgementTimeInterval,
                    outboundAcknowledgementOption: outboundAcknowledgementOption
                } as UpdatePartnerTransactionInput
            );
        }
    };

    const closeEditDialog = () => {
        onClose();
    };

    const validation = () => {
        if (isFormDirty) {
            let validTimeInterval = true;
            
            // Validate acknowledgement time interval when acknowledgement mode is Acknowledgement
            if (acknowledgementMode === FunctionalReconciliationMode.Acknowledgement) {
                if (acknowledgementTimeInterval === undefined || acknowledgementTimeInterval < 1 || acknowledgementTimeInterval > 24) {
                    validTimeInterval = false;
                }
            }
            
            return validTimeInterval;
        }
        return false;
    };

    const onError = () => {
        setSubmitted(false);
    };

    const onPipelineDialogClose = () => {
        setPipelineDialogOpen(false);
    };

    const onEditPipeline = () => {
        setPipelineDialogOpen(true);
    };

    const savePipelineFromDialogPicker = (selectedPipeline?: PipelineModel) => {
        setIsFormDirty(true);
        if (selectedPipeline) {
            setPipelineIdValue(selectedPipeline.id);
            setPipelineDescription(selectedPipeline.description as string)
        } else {
            setPipelineIdValue('');
            setPipelineDescription('');
        }
        // close the dialog after setting values
        onPipelineDialogClose();
    };

    const onAcknowledgementModeChange = (event: SelectChangeEvent<string | null>) => {
        setIsFormDirty(true);
        const newMode = event.target.value as FunctionalReconciliationMode;
        
        if (newMode === FunctionalReconciliationMode.Acknowledgement) {
            setAcknowledgementMode(newMode);
            setAcknowledgementTimeInterval(24);
        } else {
            setAcknowledgementMode(FunctionalReconciliationMode.Na);
            setAcknowledgementTimeInterval(undefined);
        }
    };

    const onAcknowledgementTimeIntervalChange = (event: ChangeEvent<HTMLInputElement>) => {
        let value = event.target.value;
        
        // Truncate to max 2 digits
        if (value.length > 2) {
            value = value.substring(0, 2);
        }
        
        const newNumValue = value === '' ? undefined : parseInt(value, 10);
        
        // Only set dirty if the value actually changed
        if (newNumValue !== acknowledgementTimeInterval) {
            setIsFormDirty(true);
        }
        
        setAcknowledgementTimeInterval(newNumValue);
    };

    const onOutboundAcknowledgementOptionChange = (event: SelectChangeEvent<string | null>) => {
        setIsFormDirty(true);
        if (event.target.value) {
            setOutboundAcknowledgementOption(event.target.value as OutboundAcknowledgementType);
        } else {
            setOutboundAcknowledgementOption(undefined);
        }
    };

    const acknowledgementModes = Object.values(FunctionalReconciliationMode);
        
    const getAcknowledgementModeOptions = () => {
        if (acknowledgementModes && acknowledgementModes.length) {
            let items = [];
            const mappedItems = (
                acknowledgementModes.map((mode: FunctionalReconciliationMode) => (
                    <MenuItem
                        key={mode}
                        value={mode}
                    >
                        {mode === FunctionalReconciliationMode.Na ? 'NA' : capitalize(mode)}
                    </MenuItem>
                ))
            );
            items.push(...mappedItems);
            return items;
        }
        return null;
    };

    const outboundAcknowledgementOptions = Object.values(OutboundAcknowledgementType);

    const getOutboundAcknowledgementOptions = () => {
        if (outboundAcknowledgementOptions && outboundAcknowledgementOptions.length) {
            let items = [];
            const mappedItems = (
                outboundAcknowledgementOptions.map((option: OutboundAcknowledgementType) => (
                    <MenuItem
                        key={option}
                        value={option}
                    >
                        {capitalize(option)}
                    </MenuItem>
                ))
            );
            items.push(...mappedItems);
            return items;
        }
        return null;
    };
    
    const getPipelineContent = () => {
        let pipelineText = pipelineDescription ? pipelineDescription : "Pipeline Not Set";
        
        return (
            <Grid>
                <DialogDynamicListHeader aria-label='selected-pipeline' justifyContent='flex-start'>Selected Pipeline</DialogDynamicListHeader>
                <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={2}>
                    <Typography variant='body1' >{pipelineText}</Typography>
                    <IconButton color="primary" aria-label="edit-Pipeline" onClick={onEditPipeline}>
                        <EditIcon />
                    </IconButton>
                </Stack>
            </Grid>
        );
    };

    const getAcknowledgementModeContent = () => {
        return (
            <Grid item xs={4}>
                <FormControl variant="standard" fullWidth>
                    <InputLabel id="dialog-edit-partner-transaction-acknowledgement-mode-label">Functional Acknowledgement Mode</InputLabel>
                    <Select
                        labelId="dialog-edit-partner-transaction-acknowledgement-mode-label"
                        aria-labelledby="dialog-edit-partner-transaction-acknowledgement-mode-label"
                        value={acknowledgementMode ?? ""}
                        onChange={onAcknowledgementModeChange}
                        disabled={submitted || partner?.functionalReconciliationMode === FunctionalReconciliationMode.Na || isFunctionalAcknowledgementTransaction}
                        MenuProps={modeProps}
                        data-cy="dialog-edit-partner-transaction-acknowledgement-mode"
                    >
                        {getAcknowledgementModeOptions()}
                    </Select>
                </FormControl>
            </Grid>
        );
    };

    const getOutboundAcknowledgementContent = () => {
        return (
            <Grid item xs={4}>
                <FormControl variant="standard" fullWidth>
                    <InputLabel id="edit-outbound-acknowledgement-option-label">Outbound Acknowledgement Option</InputLabel>
                    <Select
                        labelId="edit-outbound-acknowledgement-option-label"
                        aria-labelledby="edit-outbound-acknowledgement-option-label"
                        value={outboundAcknowledgementOption ?? OutboundAcknowledgementType.Normal}
                        onChange={onOutboundAcknowledgementOptionChange}
                        disabled={submitted}
                        MenuProps={{
                            'aria-label': 'outbound acknowledgement option',
                        }}
                        data-cy="dialog-edit-partner-transaction-outbound-acknowledgement-option"
                    >
                        {getOutboundAcknowledgementOptions()}
                    </Select>
                </FormControl>
            </Grid>
        );
    };

    const getAcknowledgementTimeIntervalContent = () => {
        return (
            <Grid item xs={4}>
                <TextField
                    type="number"
                    disabled={submitted}
                    value={acknowledgementTimeInterval ?? ''}
                    label="Acknowledgement Time Interval (Hours)"
                    inputProps={acknowledgementTimeIntervalProps}
                    onChange={onAcknowledgementTimeIntervalChange}
                    variant="standard"
                    fullWidth
                    error={acknowledgementMode === FunctionalReconciliationMode.Acknowledgement && (acknowledgementTimeInterval === undefined || acknowledgementTimeInterval < 1 || acknowledgementTimeInterval > 24)}
                    helperText={acknowledgementMode === FunctionalReconciliationMode.Acknowledgement && (acknowledgementTimeInterval === undefined || acknowledgementTimeInterval < 1 || acknowledgementTimeInterval > 24) ? 'Must be between 1 and 24 hours' : ''}
                    data-cy="dialog-edit-partner-transaction-acknowledgement-time-interval"
                />
            </Grid>
        );
    };

    const getSchemaContent = () => {
        return (
            <DialogPartnerTransactionSchemaList 
                parentId={partnerTransactionId ?? ''}
                schemas={transactionSchemas} 
                isLoading={submitted}
                isParentTransactionInbound={isInboundTransaction}
                refreshPartnerData={refreshPartnerData}
            />
        );
    };

    const modeProps = {
        'aria-label': 'functional acknowledgement mode',
    };

    const acknowledgementTimeIntervalProps = {
        'aria-label': 'acknowledgement time interval hours',
        'min': 1,
        'max': 24,
    };

    const entityNameDisplay = transactionName ? `Transaction - ${transactionName} ${capitalize(transactionDirection)}` : 'Transaction';
   
    return (
        <AddEditDialog
            isOpen={isOpen}
            isSubmitted={submitted}
            id={partnerTransactionId}
            entityName={entityNameDisplay}
            maxWidth='md'
            validate={validation}
            onClose={closeEditDialog}
            onSave={submitForm}
            onError={onError}
            error={error}
        >
            <Grid container spacing={2}>
                <DialogDataGridWrapper container>
                    {getPipelineContent()}
                </DialogDataGridWrapper>
                <DialogDataGridWrapper container mt={2} mb={2}>
                    {getAcknowledgementModeContent()}
                </DialogDataGridWrapper>
                {acknowledgementMode === FunctionalReconciliationMode.Acknowledgement && (
                <DialogDataGridWrapper container mt={2} mb={2}>
                    {getAcknowledgementTimeIntervalContent()}
                </DialogDataGridWrapper>
                )}
                {isInboundTransaction && (
                <DialogDataGridWrapper container mt={2} mb={2}>
                    {getOutboundAcknowledgementContent()}
                </DialogDataGridWrapper>
                )}
                <DialogDataGridWrapper container>
                    {getSchemaContent()}
                </DialogDataGridWrapper>
            </Grid>    
            <TransactionPipelineDialog
                isOpen={pipelineDialogOpen}
                pipeline={partnerTransaction?.pipeline as PipelineModel}
                onClose={onPipelineDialogClose}
                onSave={savePipelineFromDialogPicker}
                error={error}
            />
        </AddEditDialog>
    );
}

export default EditPartnerTransactionDialog;